import CheckIcon from "@mui/icons-material/Check";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ReactComponent as DisableIcon } from "../../../common/icons/delete.svg";
import { ReactComponent as EditIcon } from "../../../common/icons/editSmall.svg";
import { ReactElement } from "react";
import { ExpertUser } from "../../user/types/entities";

export enum MenuItemKey {
  EDIT = "editUser",
  EDIT_STATUS = "editStatus",
  DISABLE = "disable",
  ENABLE = "enable",
}

export type MenuItem = {
  key: MenuItemKey;
  icon: ReactElement;
  color?: string;
};

type Action = (user: ExpertUser | null) => void;

type Props = {
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  user: ExpertUser | null;
  onExited: () => void;
  menuActions: {
    editUser: Action;
    editStatus: Action;
    enable: Action;
    disable: Action;
  };
};

const menuItems: MenuItem[] = [
  {
    key: MenuItemKey.EDIT,
    icon: <EditIcon />,
  },
  {
    key: MenuItemKey.EDIT_STATUS,
    icon: <EventBusyIcon fontSize="small" />,
  },
  {
    key: MenuItemKey.DISABLE,
    icon: <DisableIcon />,
    color: "error.dark",
  },
  {
    key: MenuItemKey.ENABLE,
    icon: <CheckIcon />,
  },
];

export const getFilteredMenuItems = (user: ExpertUser | null): MenuItem[] => {
  if (user?.account.enabled == null || user?.account.enabled === true) {
    return menuItems.filter(
      (i) =>
        i.key === MenuItemKey.EDIT ||
        i.key === MenuItemKey.EDIT_STATUS ||
        i.key === MenuItemKey.DISABLE,
    );
  }

  return menuItems.filter(
    (i) => i.key === MenuItemKey.EDIT_STATUS || i.key === MenuItemKey.ENABLE,
  );
};

export const ExpertActionsMenu = ({
  open,
  anchorEl,
  onClose,
  user,
  onExited,
  menuActions,
}: Props) => {
  const { t } = useTranslation();

  const handleClick = (key: MenuItemKey) => {
    menuActions[key](user);
    onClose();
  };

  const filteredMenuItems = getFilteredMenuItems(user);

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      TransitionProps={{ onExited }}
    >
      <List dense disablePadding>
        {filteredMenuItems.map((menuItem) => (
          <ListItem key={menuItem.key} disablePadding>
            <ListItemButton
              sx={{
                paddingLeft: "13px",
                paddingRight: "13px",
              }}
              onClick={() => handleClick(menuItem.key)}
            >
              <ListItemIcon sx={{ minWidth: "13px", marginRight: "13px" }}>
                {menuItem.icon}
              </ListItemIcon>
              <ListItemText>
                <Typography {...(menuItem?.color && { color: menuItem.color })}>
                  {t(`settings:${menuItem.key}`)}
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Menu>
  );
};
